import { useEffect, useId, useMemo, useRef, useState } from "react";
import { dayNumber, formatFullDay, formatMonthOf } from "../format.js";
import { isDayKey, isSameMonth, monthOf, shift, WEEKDAYS, type DayKey } from "./grid.js";
import "./miniMonth.css";

/** Browse months independently; the Diary owns the selected date and list range. */
export function MiniMonth({
  selected,
  today,
  range,
  onSelectDay,
}: {
  selected: DayKey;
  today: DayKey;
  range?: { from: DayKey; to: DayKey };
  onSelectDay: (day: DayKey) => void;
}) {
  const selectedDay = isDayKey(selected) ? selected : today;
  const [month, setMonth] = useState(() => shift("month", selectedDay, 0));
  const [expanded, setExpanded] = useState(false);
  const pickerId = useId();
  const root = useRef<HTMLDivElement>(null);
  const toggle = useRef<HTMLButtonElement>(null);
  const calendar = useMemo(() => ({
    heading: formatMonthOf(month),
    days: monthOf(month).map((day) => ({
      day,
      number: dayNumber(day),
      label: formatFullDay(day),
      outside: !isSameMonth(day, month),
    })),
  }), [month]);
  const selectedLabel = useMemo(() => formatFullDay(selectedDay), [selectedDay]);
  const rangeLabel = useMemo(() => range
    ? `. List range ${formatFullDay(range.from)} to ${formatFullDay(range.to)}` : "",
  [range?.from, range?.to]);

  useEffect(() => {
    setMonth(shift("month", selectedDay, 0));
  }, [selectedDay]);

  function returnFocus() {
    // The opener is hidden on desktop, where the calendar is always visible.
    if (toggle.current?.getClientRects().length) toggle.current.focus();
  }

  useEffect(() => {
    if (!expanded) return;
    const onDown = (event: PointerEvent) => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) setExpanded(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setExpanded(false);
      returnFocus();
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [expanded]);

  return (
    <div
      className={`cal-mini-month${expanded ? " is-expanded" : ""}`}
      ref={root}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setExpanded(false);
      }}
    >
      <button
        type="button"
        className="cal-mini-toggle"
        ref={toggle}
        aria-expanded={expanded}
        aria-controls={pickerId}
        aria-label={`Choose diary date, ${selectedLabel}${rangeLabel}`}
        onClick={() => setExpanded((current) => !current)}
      >
        <span>Choose date</span>
        <span>{selectedLabel}</span>
      </button>
      <div className="cal-mini-content" id={pickerId} role="group" aria-label="Diary date picker">
        <div className="cal-mini-header">
          <button
            type="button"
            className="cal-mini-arrow"
            aria-label="Browse previous month in date picker"
            onClick={() => setMonth((current) => shift("month", current, -1))}
          >
            <span aria-hidden="true">‹</span>
          </button>
          <strong aria-live="polite" aria-atomic="true">{calendar.heading}</strong>
          <button
            type="button"
            className="cal-mini-arrow"
            aria-label="Browse next month in date picker"
            onClick={() => setMonth((current) => shift("month", current, 1))}
          >
            <span aria-hidden="true">›</span>
          </button>
        </div>
        <div className="cal-mini-days">
          {WEEKDAYS.map((weekday) => (
            <span className="cal-mini-weekday" key={weekday} aria-hidden="true">{weekday}</span>
          ))}
          {calendar.days.map(({ day, number, label, outside }) => {
            const inRange = range !== undefined && day >= range.from && day <= range.to;
            return (
              <button
                type="button"
                className={`cal-mini-day${outside ? " is-outside" : ""}${inRange ? " is-in-range" : ""}${day === selectedDay ? " is-selected" : ""}${day === today ? " is-today" : ""}`}
                key={day}
                aria-label={`Go to ${label}${day === today ? ", today" : ""}${inRange ? ", in list range" : ""}`}
                aria-current={day === selectedDay ? "date" : undefined}
                onClick={() => {
                  setMonth(shift("month", day, 0));
                  setExpanded(false);
                  returnFocus();
                  onSelectDay(day);
                }}
              >
                <time dateTime={day}>{number}</time>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
